import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import { getJobRelatedExpenseFieldsYupOnSubmitSchema } from "entities/JobRelatedExpense";
import i18n from "shared/i18n";
import * as yup from "yup";

export const parseDateString = (value, originalValue) => {
  const formatted = dayjs(value);
  return formatted.isValid() ? formatted.format("YYYY-MM-DD") : null;
};

export function getOnSubmitValidationSchema(filesValidationSchema) {
  const jobRelatedExpenseSchema = getJobRelatedExpenseFieldsYupOnSubmitSchema();
  return yupResolver(
    yup.object().shape({
      ...jobRelatedExpenseSchema,
      cost_type: yup
        .mixed()
        .test(
          "cost_type",
          i18n.t("validations.cost_type.is_required", { ns: "CostLine" }),
          (obj) => {
            return typeof obj?.value.id === "number";
          }
        ),
      currency_code: yup
        .object({
          value: yup.string(),
          label: yup.string(),
        })
        .when("cost_type", (costType, schema) =>
          schema.nullable(costType?.value.subtype === "Mileage")
        )
        .typeError(
          i18n.t("validations.currency_code.is_required", { ns: "CostLine" })
        ),
      amount: yup
        .number()
        .typeError(
          i18n.t("validations.amount.greater_than_zero", {
            ns: "CostLine",
          })
        )
        .when("cost_type", {
          is: (costType) => costType?.value.subtype === "Mileage",
          then: (schema) => schema.notRequired().nullable(),
          otherwise: (schema) =>
            schema.positive(
              i18n.t("validations.amount.greater_than_zero", {
                ns: "CostLine",
              })
            ),
        }),
      description: yup
        .string()
        .required(
          i18n.t("validations.description.is_required", { ns: "CostLine" })
        )
        .max(
          250,
          i18n.t("validations.description.max_length", { ns: "CostLine" })
        ),
      purchase_date: yup
        .string()
        .transform(parseDateString)
        .when("cost_type", {
          is: (costType) => costType?.value.subtype !== "Mileage",
          then: (schema) =>
            schema.typeError(
              i18n.t("validations.purchase_date.is_required", {
                ns: "CostLine",
              })
            ),
          otherwise: (schema) => schema.nullable(true),
        }),
      payment_method_type: yup.string().required(),
      vat: yup.mixed().when(["cost_type", "is_job_related"], {
        is: (costType, isJobRelated) =>
          costType?.value.subtype !== "Mileage" && !isJobRelated,
        then: (schema) =>
          schema.test(
            "vat",
            i18n.t("validations.vat.is_required", { ns: "CostLine" }),
            (vat) => {
              return typeof vat?.value === "number";
            }
          ),
        otherwise: (schema) => schema,
      }),
      distance: yup.string().when("cost_type", {
        is: (costType) => costType?.value.subtype === "Mileage",
        then: (schema) =>
          schema.required(
            i18n.t("validations.distance.is_required", { ns: "CostLine" })
          ),
        otherwise: (schema) => schema.nullable(true),
      }),
      vehicle_id: yup
        .number()
        .transform((_, originalValue) => originalValue?.value ?? null)
        .when("cost_type", {
          is: (costType) => costType?.value.subtype === "Mileage",
          then: (schema) => schema.nullable(false),
          otherwise: (schema) => schema.nullable(true),
        })
        .typeError(
          i18n.t("validations.vehicle.is_required", { ns: "CostLine" })
        ),
      attachments: filesValidationSchema.test(
        "files",
        i18n.t("validations.attachments.is_required", { ns: "CostLine" }),
        function (files) {
          if (!files?.length) return false;
          const sortedFiles = files.reduce(
            (acc, file, idx, self) => {
              if (file.action === "updateAttachment") {
                acc.filesToUpdate.push(file);
              } else if (file.action === "storeAttachment") {
                acc.filesToStore.push(file);
              } else if (file.action === "deleteAttachment") {
                if (
                  self?.some(
                    (f) => f.action === "updateAttachment" && f.id === file.id
                  )
                )
                  return acc;
                acc.filesToDelete.push(file);
              } else {
                acc.stored.push(file);
              }
              return acc;
            },
            {
              filesToUpdate: [],
              filesToStore: [],
              filesToDelete: [],
              stored: [],
            }
          );
          const filesLength = sortedFiles.stored.concat(
            sortedFiles.filesToStore,
            sortedFiles.filesToUpdate
          );
          return filesLength.length >= 1;
        }
      ),
      credit_card_payment: yup.boolean(),
    })
  );
}
